import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

import { adminApi } from "@/lib/api";
import {
  AdminInput,
  AdminSelect,
  AdminTextarea,
  ConfirmDialog,
  EmptyState,
  FieldLabel,
  Modal,
  StatusBadge,
} from "@/components/admin/AdminUI";
import ImageUploadField from "@/components/admin/ImageUploadField";

const EMPTY = {
  name: "",
  role: "",
  short_bio: "",
  full_bio: "",
  portrait: "",
  hover_image: "",
  gallery: [],
  credentials: "",
  sort_order: 0,
  status: "published",
};

/*
  Main portraits:
  - portrait = B/W image for homepage default state
  - hover_image = color image for homepage hover state

  About Us gallery:
  - gallery = color-only images
  - About Us must not use B/W hover behavior
*/
function normalizeGallery(items) {
  return (items || [])
    .map((item) => {
      if (!item) return null;

      if (typeof item === "string") {
        return { image: item };
      }

      const image =
        item.image ||
        item.color ||
        item.url ||
        item.hoverImage ||
        item.hover_image ||
        "";

      return { image };
    })
    .filter((item) => item.image);
}

function normalizeMemberForEdit(member) {
  return {
    ...EMPTY,
    ...member,
    hover_image: member.hover_image || member.hoverImage || "",
    gallery: normalizeGallery(member.gallery),
  };
}

export default function AdminTeam() {
  const [items, setItems] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [current, setCurrent] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => adminApi.listTeam().then(setItems);

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setCurrent(EMPTY);
    setOpenForm(true);
  };

  const openEdit = (member) => {
    setCurrent(normalizeMemberForEdit(member));
    setOpenForm(true);
  };

  const save = async (event) => {
    event.preventDefault();

    try {
      const payload = {
        ...current,
        sort_order: Number(current.sort_order) || 0,

        // Keep About Us gallery color-only.
        gallery: normalizeGallery(current.gallery),
      };

      if (current.id) {
        await adminApi.updateTeam(current.id, payload);
      } else {
        await adminApi.createTeam(payload);
      }

      toast.success("Saved");
      setOpenForm(false);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;

    try {
      await adminApi.deleteTeam(confirmDelete.id);
      toast.success("Deleted");
      load();
      setConfirmDelete(null);
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="body-text">
          The people behind Minga. Public team is limited to what you publish here.
        </p>

        <button type="button" onClick={openCreate} className="btn-primary">
          <Plus className="h-4 w-4" />
          Add Member
        </button>
      </div>

      {items.length === 0 ? (
        <EmptyState title="No team members yet" />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((member) => (
            <div key={member.id} className="admin-card rounded-sm overflow-hidden">
              <div className="aspect-[3/4] bg-ink-graphite overflow-hidden">
                {member.hover_image || member.hoverImage || member.portrait ? (
                  <img
                    src={member.hover_image || member.hoverImage || member.portrait}
                    alt={member.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full grid place-items-center text-brand-gray text-xs uppercase tracking-widest">
                    No portrait
                  </div>
                )}
              </div>

              <div className="p-4 md:p-5">
                <div className="flex items-center justify-between">
                  <p className="eyebrow-muted">{member.role}</p>
                  <StatusBadge status={member.status} />
                </div>

                <h3 className="text-lg font-semibold mt-1">
                  {member.name}
                </h3>

                <p className="body-text text-xs mt-2 line-clamp-3">
                  {member.short_bio}
                </p>

                <div className="mt-4 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => openEdit(member)}
                    className="btn-outline text-[10px] px-3 py-2"
                  >
                    Edit
                  </button>

                  <button
                    type="button"
                    onClick={() => setConfirmDelete(member)}
                    className="text-brand-gray hover:text-brand-red"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={openForm}
        onClose={() => setOpenForm(false)}
        title={current.id ? "Edit member" : "New member"}
        wide
      >
        <form onSubmit={save} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <label>
              <FieldLabel>Name</FieldLabel>
              <AdminInput
                required
                value={current.name}
                onChange={(event) =>
                  setCurrent({ ...current, name: event.target.value })
                }
              />
            </label>

            <label>
              <FieldLabel>Role</FieldLabel>
              <AdminInput
                required
                value={current.role}
                onChange={(event) =>
                  setCurrent({ ...current, role: event.target.value })
                }
              />
            </label>

            <div className="md:col-span-2 grid md:grid-cols-2 gap-4 border border-white/8 rounded-sm p-4">
              <div>
                <FieldLabel>Homepage portrait B/W</FieldLabel>
                <ImageUploadField
                  value={current.portrait || ""}
                  onChange={(value) =>
                    setCurrent({ ...current, portrait: value })
                  }
                  category="team"
                />
                <p className="text-[11px] text-brand-gray mt-1">
                  Used only on the homepage as the default black-and-white image.
                </p>
              </div>

              <div>
                <FieldLabel>Homepage portrait color</FieldLabel>
                <ImageUploadField
                  value={current.hover_image || current.hoverImage || ""}
                  onChange={(value) =>
                    setCurrent({ ...current, hover_image: value })
                  }
                  category="team"
                />
                <p className="text-[11px] text-brand-gray mt-1">
                  Used only on the homepage when hovering over the portrait.
                </p>
              </div>
            </div>

            <label>
              <FieldLabel>Credentials</FieldLabel>
              <AdminInput
                value={current.credentials || ""}
                onChange={(event) =>
                  setCurrent({ ...current, credentials: event.target.value })
                }
              />
            </label>
          </div>

          <label>
            <FieldLabel>Short bio</FieldLabel>
            <AdminTextarea
              rows={4}
              value={current.short_bio || ""}
              onChange={(event) =>
                setCurrent({ ...current, short_bio: event.target.value })
              }
            />
          </label>

          <label>
            <FieldLabel>Full bio</FieldLabel>
            <AdminTextarea
              rows={6}
              value={current.full_bio || ""}
              onChange={(event) =>
                setCurrent({ ...current, full_bio: event.target.value })
              }
            />
          </label>

          <div className="space-y-3 border border-white/8 rounded-sm p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <FieldLabel>About Us gallery</FieldLabel>
                <p className="text-[11px] text-brand-gray mt-1">
                  Add normal color images for the About Us team gallery. No B/W hover effect is used here.
                </p>
              </div>

              <button
                type="button"
                className="btn-outline text-[10px] px-3 py-2"
                onClick={() =>
                  setCurrent({
                    ...current,
                    gallery: [...(current.gallery || []), { image: "" }],
                  })
                }
              >
                Add image
              </button>
            </div>

            {(current.gallery || []).map((item, index) => (
              <div
                key={index}
                className="grid md:grid-cols-[1fr_auto] gap-3 items-end"
              >
                <div>
                  <FieldLabel>Image {index + 1}</FieldLabel>
                  <ImageUploadField
                    value={item.image || item.color || item.url || ""}
                    category="team"
                    onChange={(value) => {
                      const arr = [...(current.gallery || [])];
                      arr[index] = { image: value };
                      setCurrent({ ...current, gallery: arr });
                    }}
                  />
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setCurrent({
                      ...current,
                      gallery: (current.gallery || []).filter(
                        (_, itemIndex) => itemIndex !== index
                      ),
                    })
                  }
                  className="h-10 w-10 grid place-items-center border border-white/10 rounded-sm text-brand-red"
                  aria-label={`Remove image ${index + 1}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <label>
              <FieldLabel>Sort order</FieldLabel>
              <AdminInput
                type="number"
                value={current.sort_order || 0}
                onChange={(event) =>
                  setCurrent({ ...current, sort_order: event.target.value })
                }
              />
            </label>

            <label>
              <FieldLabel>Status</FieldLabel>
              <AdminSelect
                value={current.status}
                onChange={(event) =>
                  setCurrent({ ...current, status: event.target.value })
                }
              >
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </AdminSelect>
            </label>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="btn-outline"
              onClick={() => setOpenForm(false)}
            >
              Cancel
            </button>

            <button type="submit" className="btn-primary">
              Save
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={doDelete}
        title="Remove team member"
        description={`Remove ${confirmDelete?.name}?`}
      />
    </div>
  );
}